if (!customElements.get('monument-navigation-v5')) {
  class MonumentNavigationV5 extends HTMLElement {
    constructor() {
      super();
      this.breakpoint = Number(this.dataset.breakpoint || 1120);
      this.triggers = Array.from(this.querySelectorAll('[data-m5-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-m5-panel]'));
      this.items = Array.from(this.querySelectorAll('[data-m5-item]'));
      this.desktopBar = this.querySelector('[data-m5-desktop]');
      this.drawer = this.querySelector('[data-m5-drawer]');
      this.drawerToggle = this.querySelector('[data-m5-drawer-open]');
      this.drawerCloseButtons = Array.from(this.querySelectorAll('[data-m5-drawer-close]'));
      this.openTrigger = null;
      this.hoverTimer = null;
      this.previousBodyOverflow = '';

      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
      this.onDocumentFocusIn = this.onDocumentFocusIn.bind(this);
      this.updatePanelPosition = this.updatePanelPosition.bind(this);
      this.onResize = this.onResize.bind(this);

      this.bindDesktop();
      this.bindDrawer();
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener('keydown', this.onDocumentKeyDown);
      document.addEventListener('focusin', this.onDocumentFocusIn);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('scroll', this.updatePanelPosition, { passive: true });
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('keydown', this.onDocumentKeyDown);
      document.removeEventListener('focusin', this.onDocumentFocusIn);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.updatePanelPosition);
      window.clearTimeout(this.hoverTimer);
      if (this.drawer && !this.drawer.hidden) this.closeDrawer(false);
    }

    isDesktop() {
      return window.innerWidth > this.breakpoint;
    }

    bindDesktop() {
      this.triggers.forEach((trigger, index) => {
        const item = trigger.closest('[data-m5-item]');
        trigger.addEventListener('click', () => {
          if (!this.isDesktop()) return;
          trigger === this.openTrigger ? this.closePanels() : this.openPanel(trigger);
        });
        trigger.addEventListener('keydown', (event) => this.onTriggerKeyDown(event, index));
        trigger.addEventListener('focus', () => {
          if (this.isDesktop()) this.openPanel(trigger);
        });
        if (item) {
          item.addEventListener('pointerenter', () => {
            if (!this.isDesktop()) return;
            window.clearTimeout(this.hoverTimer);
            this.hoverTimer = window.setTimeout(() => this.openPanel(trigger), 110);
          });
          item.addEventListener('pointerleave', () => {
            window.clearTimeout(this.hoverTimer);
            this.hoverTimer = window.setTimeout(() => this.closePanels(), 170);
          });
        }
      });
    }

    bindDrawer() {
      if (!this.drawer || !this.drawerToggle) return;
      this.drawerToggle.addEventListener('click', () => this.openDrawer());
      this.drawerCloseButtons.forEach((button) => button.addEventListener('click', () => this.closeDrawer()));
    }

    panelFor(trigger) {
      const id = trigger.getAttribute('aria-controls');
      return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    }

    openPanel(trigger) {
      if (!this.isDesktop()) return;
      const panel = this.panelFor(trigger);
      if (!panel) return;
      this.closePanels(trigger);
      this.updatePanelPosition();
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      trigger.closest('[data-m5-item]')?.classList.add('is-open');
      this.openTrigger = trigger;
    }

    closePanels(exceptTrigger = null) {
      this.triggers.forEach((trigger) => {
        if (trigger === exceptTrigger) return;
        const panel = this.panelFor(trigger);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.closest('[data-m5-item]')?.classList.remove('is-open');
        if (panel) {
          panel.hidden = true;
          panel.setAttribute('aria-hidden', 'true');
        }
      });
      if (!exceptTrigger) this.openTrigger = null;
    }

    updatePanelPosition() {
      if (!this.openTrigger || !this.desktopBar || !this.isDesktop()) return;
      const bottom = Math.max(0, Math.round(this.desktopBar.getBoundingClientRect().bottom));
      const navLeft = this.getBoundingClientRect().left;
      const viewportCenterWithinNav = Math.round((window.innerWidth / 2) - navLeft);
      this.style.setProperty('--m5-panel-top', `${bottom}px`);
      this.style.setProperty('--m5-panel-left', `${viewportCenterWithinNav}px`);
    }

    onTriggerKeyDown(event, index) {
      if (!this.isDesktop()) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const next = (index + direction + this.triggers.length) % this.triggers.length;
        this.triggers[next]?.focus();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.openPanel(event.currentTarget);
        const panel = this.panelFor(event.currentTarget);
        panel?.querySelector('a, button')?.focus();
      }
    }

    onDocumentPointerDown(event) {
      if (this.openTrigger && !this.contains(event.target)) this.closePanels();
    }

    onDocumentFocusIn(event) {
      if (this.openTrigger && !this.contains(event.target)) this.closePanels();
    }

    onDocumentKeyDown(event) {
      if (event.key !== 'Escape') return;
      if (this.drawer && !this.drawer.hidden) {
        this.closeDrawer();
      } else if (this.openTrigger) {
        const trigger = this.openTrigger;
        this.closePanels();
        trigger.focus();
      }
    }

    openDrawer() {
      if (!this.drawer) return;
      this.closePanels();
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.drawer.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawerToggle.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        this.drawer.querySelector('.m5-drawer__panel')?.focus();
      });
    }

    closeDrawer(restoreFocus = true) {
      if (!this.drawer || this.drawer.hidden) return;
      this.drawer.classList.remove('is-open');
      this.drawer.hidden = true;
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawerToggle?.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = this.previousBodyOverflow;
      if (restoreFocus) this.drawerToggle?.focus();
    }

    onResize() {
      if (this.isDesktop()) {
        if (this.drawer && !this.drawer.hidden) this.closeDrawer(false);
        this.updatePanelPosition();
      } else {
        this.closePanels();
      }
    }
  }

  customElements.define('monument-navigation-v5', MonumentNavigationV5);
}
